import type { AdminPlayerPage } from "@clubap/shared";
import { API_URL as API_BASE } from "./backend.js";

const TOKEN_KEY = "clubap.token";

export interface Account {
  id: string;
  username: string;
  color: number;
  coins: number;
  /** Cuenta de desarrollo: enciende rótulos de diagnóstico. No da permisos. */
  dev: boolean;
  /** Correo de recuperación, o `null` si no puso ninguno. */
  email: string | null;
  /** Sin confirmar, el correo está guardado pero NO recupera la cuenta. */
  emailVerified: boolean;
  /** Autoridad de verdad: cambia y borra cuentas ajenas. */
  admin: boolean;
  /** Ids del catálogo de `@clubap/shared`, o null si no lo llenó. */
  faculty: string | null;
  program: string | null;
  semester: number | null;
  /** La línea que escribe el jugador en su carnet, o null si no puso ninguna. */
  bio: string | null;
}

/** Lo que se ve de otra persona en su ficha. */
export interface PublicProfile {
  id: string;
  username: string;
  color: number;
  faculty: string | null;
  program: string | null;
  semester: number | null;
  /** Alta de la cuenta, en milisegundos. */
  createdAt: number;
  /** La línea que escribe el jugador en su carnet, o null si no puso ninguna. */
  bio: string | null;
}

export interface Credentials {
  username: string;
  password: string;
  /** Sólo al registrarse, y opcional: sin él no hay autoservicio de recuperación. */
  email?: string;
}

/**
 * Errores que el servidor puede devolver, traducidos acá para que la UI no tenga
 * que conocer códigos HTTP.
 */
const MESSAGES: Record<string, string> = {
  invalid_username: "Ese nombre no se puede usar. Entre 2 y 16 caracteres, sin groserías.",
  too_short: "La contraseña necesita al menos 8 caracteres.",
  too_long: "Esa contraseña es demasiado larga.",
  username_taken: "Ese nombre ya está tomado.",
  invalid_credentials: "Nombre o contraseña incorrectos.",
  rate_limited: "Demasiadas peticiones. Espera un minuto.",
  account_locked: "Demasiados intentos fallidos con esta cuenta. Espera 10 minutos.",
  unauthenticated: "Tu sesión venció. Vuelve a entrar.",
  insufficient_funds: "No te alcanzan las monedas.",
  already_owned: "Ya tienes ese ítem.",
  unknown_item: "Ese ítem no existe.",
  implausible_score: "Ese puntaje no es creíble para el tiempo jugado.",
  already_finished: "Esa partida ya se cobró.",
  expired: "La partida caducó.",
  invalid_score: "Puntaje inválido.",
  invalid_email: "Ese correo no parece válido.",
  email_taken: "Ese correo ya está en uso por otra cuenta.",
  /* El enlace no dice CUÁL de las cuatro cosas falló — el servidor tampoco lo distingue,
     a propósito— así que el mensaje las cubre todas y ofrece la salida. */
  invalid_token: "Ese enlace ya no sirve: puede haber vencido o haberse usado. Pide otro.",
  email_changed: "Ese enlace era para otro correo. Vuelve a pedir la confirmación.",
  mail_rate_limited: "Ya se enviaron varios correos a esa dirección. Espera 15 minutos.",
  forbidden: "No tienes permiso para hacer eso.",
  unknown_player: "Esa cuenta no existe.",
  unknown_faculty: "Esa facultad no existe.",
  unknown_program: "Esa carrera no es de esa facultad.",
  invalid_semester: "El semestre va del 1 al 10.",
  already_deleted: "Esa cuenta ya estaba borrada.",
  not_deleted: "Esa cuenta no está borrada.",

  // Sistema social. Los códigos los define `SocialRejection` en shared.
  self: "No puedes hacer eso contigo mismo.",
  already_friends: "Ya son amigos.",
  already_requested: "Ya le enviaste una solicitud.",
  not_friends: "Sólo puedes escribirle a tus amigos.",
  unknown_request: "Esa solicitud ya no está.",
  not_yours: "Esa solicitud no es tuya.",
  unknown_club: "Ese club no existe.",
  already_member: "Ya estás en ese club.",
  not_member: "No estás en ese club.",
  too_many_clubs: "Estás en demasiados clubes. Sal de uno primero.",
  too_many_friends: "Esa lista de amigos está llena.",
  too_many_requests: "Tienes demasiadas solicitudes sin responder.",
  message_too_long: "Ese mensaje es demasiado largo.",
  filtered: "Ese mensaje no se puede enviar.",
  empty: "Escribe algo primero.",
  bad_cursor: "No se pudo cargar esa parte de la conversación.",
  query_too_short: "Escribe al menos dos letras para buscar.",
  unknown_furniture: "Ese mueble no existe.",
  unknown_placement: "Ese mueble ya no está puesto.",
  none_left: "No te queda ninguno sin poner. Compra otro.",
  too_many: "Tu iglú ya está lleno.",
  /* El servidor manda además el motivo exacto en `message`; esto es el respaldo para
     cuando no lo haya. */
  invalid_placement: "Ahí no cabe.",

  /*
   * Los rechazos de club, que faltaban enteros.
   *
   * Cada uno de estos salía como "Algo falló. Intenta de nuevo." — que es lo que dice
   * esta tabla cuando no reconoce un código. El jugador pedía entrar a un club, se le
   * negaba por una razón concreta y perfectamente explicable, y leía un error genérico
   * que no le decía qué hacer. Una tabla de traducción que no cubre su original no avisa
   * de nada: simplemente calla en el idioma equivocado.
   */
  not_in_club: "Esa persona no está en el club.",
  not_president: "Eso sólo lo puede hacer quien preside el club.",
  already_president: "Esa persona ya preside el club.",
  unknown_club_request: "Esa solicitud ya no está.",
  already_requested_club: "Ya pediste entrar a ese club.",
  too_many_club_requests: "Tienes demasiadas solicitudes de ingreso esperando.",

  /* Y los de fundar, que el formulario necesita para poder corregirse solo. */
  name_taken: "Ya hay un club con ese nombre.",
  code_taken: "Ya hay un club con esas siglas.",
  already_proposed: "Ya tienes una solicitud esperando. Retírala para mandar otra.",
  already_decided: "Esa solicitud ya se respondió.",
  unknown_proposal: "Esa solicitud ya no está.",
  bad_color: "Ese color no vale.",
  bad_logo: "Ese logo no se puede usar: sube un SVG o un PNG.",
  logo_too_big: "El logo pesa demasiado. El tope son 64 KB.",

  /* Y los del evento, por lo mismo. */
  unknown_event: "Ese evento ya no está.",
  unknown_room: "Ahí no se puede convocar.",
  bad_window: "Esa duración no vale.",
  too_soon: "Esa hora ya pasó.",
  too_far: "No se puede convocar tan lejos.",
  too_many_events: "El club ya tiene demasiados eventos por delante.",
  already_started: "Ese evento ya empezó.",
  invalid_amount: "Esa cantidad no vale.",
  unknown_style: "Ese piso o esa pared ya no existen.",
};

export class ApiError extends Error {
  constructor(public code: string) {
    super(MESSAGES[code] ?? "Algo falló. Intenta de nuevo.");
  }
}

export function storedToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function rememberToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function forgetToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function post(path: string, body: unknown, token?: string): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
}

async function readError(response: Response): Promise<never> {
  let code = "unknown";
  try {
    code = ((await response.json()) as { error?: string }).error ?? "unknown";
  } catch {
    // Cuerpo no-JSON (proxy caído, 502): el mensaje genérico basta.
  }
  throw new ApiError(code);
}

async function authenticate(path: string, credentials: Credentials): Promise<Account> {
  const response = await post(path, credentials);
  if (!response.ok) await readError(response);

  const data = (await response.json()) as { token: string; player: Account };
  rememberToken(data.token);

  return data.player;
}

export function register(credentials: Credentials): Promise<Account> {
  return authenticate("/api/register", credentials);
}

export function login(credentials: Credentials): Promise<Account> {
  return authenticate("/api/login", credentials);
}

/**
 * Reanuda la sesión guardada. Si el token venció se descarta acá, para que la app
 * no arranque creyendo que hay sesión cuando no la hay.
 */
export async function resumeSession(): Promise<Account | null> {
  const token = storedToken();
  if (!token) return null;

  try {
    const response = await fetch(`${API_BASE}/api/me`, {
      headers: { authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      forgetToken();
      return null;
    }

    return ((await response.json()) as { player: Account }).player;
  } catch {
    // Servidor caído: se conserva el token para reintentar más tarde.
    return null;
  }
}

export interface WardrobeState {
  items: Array<{ id: string; name: string; slot: string; price: number }>;
  owned: string[];
  equipped: string[];
  balance: number;
}

async function authed(path: string, body?: unknown): Promise<Response> {
  const token = storedToken();

  return fetch(`${API_BASE}${path}`, {
    method: body === undefined ? "GET" : "POST",
    headers: {
      ...(body === undefined ? {} : { "content-type": "application/json" }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

/**
 * Petición autenticada que no devuelve nada útil (204). El error se traduce igual.
 *
 * Se exporta junto a `authedJson` para que un módulo nuevo no tenga que reinventar
 * el manejo del token ni el de los códigos: pasar el `Bearer` a mano en cada sitio
 * es exactamente cómo se acaba con un endpoint que se olvidó de mandarlo.
 */
export async function authedRequest(path: string, body?: unknown): Promise<void> {
  const response = await authed(path, body);
  if (!response.ok) await readError(response);
}

/** Igual, pero con cuerpo JSON tipado. Sin `body` es un GET. */
export async function authedJson<T>(path: string, body?: unknown): Promise<T> {
  const response = await authed(path, body);
  if (!response.ok) await readError(response);
  return (await response.json()) as T;
}

/**
 * El listado de cuentas de la vista de administración.
 *
 * Responde 403 a quien no sea admin, así que un fallo acá no es un caso raro: es lo
 * que pasa si alguien llega al panel sin la marca. El panel enseña el mensaje.
 */
export function fetchAdminPlayers(input: {
  filter: string;
  q: string;
  page: number;
}): Promise<AdminPlayerPage> {
  const params = new URLSearchParams({
    filter: input.filter,
    page: String(input.page),
    ...(input.q ? { q: input.q } : {}),
  });

  return authedJson<AdminPlayerPage>(`/api/admin/players?${params.toString()}`);
}

/** Poner o quitar `dev`. `admin` NO tiene equivalente: se da con el script. */
export function setPlayerDev(playerId: string, on: boolean): Promise<{ id: string; dev: boolean }> {
  return authedJson<{ id: string; dev: boolean }>(
    `/api/admin/players/${encodeURIComponent(playerId)}/dev`,
    { on },
  );
}

/**
 * Catálogo, lo que posees, lo que llevas puesto y tu saldo, en un viaje. La tienda
 * y el inventario son dos vistas de lo mismo, así que comparten el endpoint.
 */
export async function fetchWardrobe(): Promise<WardrobeState> {
  const response = await authed("/api/shop");
  if (!response.ok) await readError(response);
  return (await response.json()) as WardrobeState;
}

export interface PurchaseResponse {
  balance: number;
  owned: string[];
}

export async function buyItem(itemId: string): Promise<PurchaseResponse> {
  const response = await authed("/api/shop/buy", { itemId });
  if (!response.ok) await readError(response);
  return (await response.json()) as PurchaseResponse;
}

export interface GameStart {
  sessionId: string;
  seed: number;
  game: { id: string; name: string; maxScore: number; maxSeconds: number };
}

export async function startGame(gameId: string): Promise<GameStart> {
  const response = await authed(`/api/games/${gameId}/start`, {});
  if (!response.ok) await readError(response);
  return (await response.json()) as GameStart;
}

export interface GameResult {
  score: number;
  coinsAwarded: number;
  balance: number;
  cappedByLimit: boolean;
}

export async function finishGame(sessionId: string, score: number): Promise<GameResult> {
  const response = await authed("/api/games/finish", { sessionId, score });
  if (!response.ok) await readError(response);
  return (await response.json()) as GameResult;
}

/**
 * Pide el enlace de restablecimiento. `account` es el correo o el nombre de la cuenta.
 *
 * No devuelve nada y **no falla si la cuenta no existe**: el servidor responde igual
 * exista o no, para que este formulario no sirva de detector de cuentas. La UI tiene que
 * decir lo mismo en los dos casos — "si esa cuenta existe, te llegó un correo"— o
 * reintroduce por delante la fuga que el servidor cierra por detrás.
 */
export async function requestPasswordReset(account: string): Promise<void> {
  const response = await post("/api/forgot", { account });
  // El 429 sí se propaga: no dice nada de la cuenta y el usuario necesita saber que
  // tiene que esperar en vez de seguir pulsando.
  if (!response.ok) await readError(response);
}

/** Canjea el enlace del correo y fija la contraseña nueva. */
export async function resetPassword(token: string, password: string): Promise<void> {
  const response = await post("/api/reset", { token, password });
  if (!response.ok) await readError(response);
}

/** Canjea el enlace de confirmación de correo. */
export async function verifyEmail(token: string): Promise<void> {
  const response = await post("/api/email/verify", { token });
  if (!response.ok) await readError(response);
}

/**
 * Pone o cambia el correo de recuperación. Pide la contraseña aunque haya sesión.
 *
 * Devuelve la cuenta actualizada y si el correo de confirmación salió. Las dos cosas
 * importan en pantalla: la primera para reflejar la dirección nueva, la segunda para no
 * decir "te mandamos un correo" cuando el proveedor lo rechazó.
 */
export async function setRecoveryEmail(
  email: string,
  password: string,
): Promise<{ player: Account; sent: boolean }> {
  const response = await post("/api/email", { email, password }, storedToken() ?? undefined);
  if (!response.ok) await readError(response);
  return (await response.json()) as { player: Account; sent: boolean };
}

/** La ficha de otro jugador. Se busca por nombre: es lo que el estado de la sala trae. */
export async function fetchProfile(name: string): Promise<PublicProfile> {
  const response = await authed(`/api/profile/${encodeURIComponent(name)}`);
  if (!response.ok) await readError(response);
  return ((await response.json()) as { profile: PublicProfile }).profile;
}

export interface ProfilePatch {
  username?: string;
  faculty?: string | null;
  program?: string | null;
  semester?: number | null;
  /** Vacía o null la borra. El servidor la pasa por el filtro del chat. */
  bio?: string | null;
  /** ÍNDICE en `AVATAR_COLORS`, no el color: la lista es append-only. */
  color?: number;
  /** Sólo un admin puede mandarlo: cambia la cuenta de otra persona. */
  playerId?: string;
}

export interface ProfileUpdate {
  player: Account;
  /** Cambió el nombre: hay que reconectar para que la sala lo relea. */
  renamed: boolean;
  /** Cambió el color: igual que el nombre, la sala lo lee sólo al entrar. */
  recolored: boolean;
  self: boolean;
}

export async function updateProfile(patch: ProfilePatch): Promise<ProfileUpdate> {
  const response = await authed("/api/profile", patch);
  if (!response.ok) await readError(response);
  return (await response.json()) as ProfileUpdate;
}

/** Sin `playerId` borra la mía, y entonces la contraseña es obligatoria. */
export async function deleteAccount(input: {
  password?: string;
  playerId?: string;
}): Promise<void> {
  const response = await authed("/api/profile/delete", input);
  if (!response.ok) await readError(response);
}

export async function restoreAccount(playerId: string): Promise<void> {
  const response = await authed("/api/profile/restore", { playerId });
  if (!response.ok) await readError(response);
}

export async function logout(): Promise<void> {
  const token = storedToken();
  forgetToken();

  if (!token) return;
  try {
    await post("/api/logout", {}, token);
  } catch {
    // El token local ya se borró; que el servidor no responda no cambia nada.
  }
}
